// Snap-to-Grid + Smart-Guides (V2 Slice 4).
//
// Snap-to-Grid: rastet einen Punkt auf das naechste Vielfache von `gridSize` (default 8 px).
// Smart-Guides: liefert Guide-Linien wenn die Drag-Bounding-Box mit Edge/Center anderer
// Objekte alignt (±tolerance px). Caller (Canvas) rendert sie als rote 1px-Linien.

import type { CanvasObject } from "@ms-whiteboard/shared";
import { objectBounds } from "./object-bounds.js";

export const SNAP_GRID_SIZE = 8;
export const SMART_GUIDE_TOLERANCE = 4;

export function snapToGrid(value: number, gridSize = SNAP_GRID_SIZE): number {
  return Math.round(value / gridSize) * gridSize;
}

export interface Bounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type GuideAxis = "vertical" | "horizontal";

export interface SmartGuide {
  axis: GuideAxis;
  /** Worldspace-Koordinate der Linie. Bei vertical: x; bei horizontal: y. */
  position: number;
  /** Range entlang der senkrechten Achse (zum Renderen). */
  from: number;
  to: number;
  /** Vorgeschlagene Korrektur fuer die Drag-Box (delta in der Guide-Achse). */
  snapDelta: number;
}

/**
 * Findet Smart-Guides zwischen der Drag-Box und den anderen Objekten.
 * Liefert max. 1 vertikale + 1 horizontale Guide (die mit kleinstem snapDelta).
 */
export function findGuides(
  drag: Bounds,
  others: readonly CanvasObject[],
  tolerance = SMART_GUIDE_TOLERANCE,
): SmartGuide[] {
  const dragLeft = drag.x;
  const dragRight = drag.x + drag.width;
  const dragCenterX = drag.x + drag.width / 2;
  const dragTop = drag.y;
  const dragBottom = drag.y + drag.height;
  const dragCenterY = drag.y + drag.height / 2;

  let bestV: SmartGuide | null = null;
  let bestH: SmartGuide | null = null;

  for (const obj of others) {
    const b = objectBounds(obj);
    const oLeft = obj.x;
    const oRight = obj.x + b.w;
    const oCenterX = obj.x + b.w / 2;
    const oTop = obj.y;
    const oBottom = obj.y + b.h;
    const oCenterY = obj.y + b.h / 2;

    // Vertikale Guides (X-Match): drag-edge vs. other-edge
    const vChecks: Array<{ dragVal: number; otherVal: number }> = [
      { dragVal: dragLeft, otherVal: oLeft },
      { dragVal: dragLeft, otherVal: oRight },
      { dragVal: dragRight, otherVal: oLeft },
      { dragVal: dragRight, otherVal: oRight },
      { dragVal: dragCenterX, otherVal: oCenterX },
    ];
    for (const { dragVal, otherVal } of vChecks) {
      const diff = otherVal - dragVal;
      if (Math.abs(diff) > tolerance) continue;
      const next: SmartGuide = {
        axis: "vertical",
        position: otherVal,
        from: Math.min(dragTop, oTop),
        to: Math.max(dragBottom, oBottom),
        snapDelta: diff,
      };
      if (!bestV || Math.abs(diff) < Math.abs(bestV.snapDelta)) bestV = next;
    }

    // Horizontale Guides (Y-Match)
    const hChecks: Array<{ dragVal: number; otherVal: number }> = [
      { dragVal: dragTop, otherVal: oTop },
      { dragVal: dragTop, otherVal: oBottom },
      { dragVal: dragBottom, otherVal: oTop },
      { dragVal: dragBottom, otherVal: oBottom },
      { dragVal: dragCenterY, otherVal: oCenterY },
    ];
    for (const { dragVal, otherVal } of hChecks) {
      const diff = otherVal - dragVal;
      if (Math.abs(diff) > tolerance) continue;
      const next: SmartGuide = {
        axis: "horizontal",
        position: otherVal,
        from: Math.min(dragLeft, oLeft),
        to: Math.max(dragRight, oRight),
        snapDelta: diff,
      };
      if (!bestH || Math.abs(diff) < Math.abs(bestH.snapDelta)) bestH = next;
    }
  }

  const result: SmartGuide[] = [];
  if (bestV) result.push(bestV);
  if (bestH) result.push(bestH);
  return result;
}
